import { mapNumber } from "@/helpers/utility/MathHelper";
import { useIndexStore } from "../index/indices.store";
import { useTargetStore } from "../target/targets.store";
import { useRelationStore } from "../relation/relations.store";
import { useFlowBranchStore } from "../flow/flowBranches.store";
import { useEffectStore } from "../effect/effects.store";
import { useConditionStore } from "../condition/conditions.store";
import { useResourceStore } from "../resource/resources.store";
import { useSceneNodesStore } from "../scene/sceneNodes.store";
import type { IndexItem } from "../index/IndexItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { TargetItem } from "../target/TargetItem";
import type { ExpressionValue } from "../expression/expressionFunctions";
import type { ItemOfStore } from "../ItemOfStore";
import { useFlowStore } from "../flow/flows.store";

/**
 * Converts an element of one type to an element of another type.
 */
export async function changeElementType<T extends TargetItem | ResourceItem | IndexItem, NT extends "target" | "resource" | "index">(element: T, newType: NT): Promise<ItemOfStore<NT>> {
  if (element.type === newType) {
    throw new Error(`Element is already of the desired type ${newType}`);
  }

  let newElement;
  const sceneNodesStore = useSceneNodesStore();

  const calculatedValue = getElementCalculatedValue(element);

  const allElementNodes = sceneNodesStore.byNodeType["element"];
  const elementNodes = allElementNodes.filter((node) => node.data.elementUid === element.uid);

  if (newType == "resource") {
    const resourceStore = useResourceStore();

    newElement = await resourceStore.create({
      title: element.title,
      description: element.description,
      value: Number(calculatedValue.value),
      minValue: calculatedValue.minValue,
      maxValue: calculatedValue.maxValue,
      unit: calculatedValue.unit
    });
  }

  if (newType == "target") {
    const targetStore = useTargetStore();

    let { value, minValue, maxValue } = calculatedValue;

    if (minValue == maxValue || minValue == null || maxValue == null) {
      minValue = 0;
      maxValue = 100;
      value = Math.max(0, Math.min(100, Number(value)));
    }

    newElement = await targetStore.create({
      title: element.title,
      description: element.description,
      value: mapNumber(Number(calculatedValue.value), minValue, maxValue, 0, 100),
      minValue: 0,
      maxValue: 100,
      unit: "%"
    });
  }

  else if (newType == "index") {
    const indexStore = useIndexStore();

    newElement = await indexStore.create({
      title: element.title,
      description: element.description,
      valueExpression: `${calculatedValue.value}`,
      unit: element.unit
    });
  }

  if (newElement == null) {
    throw new Error("Element could not be created");
  }

  // reconnect all scene nodes to the new element
  for (const node of elementNodes) {
    sceneNodesStore.update({
      id: node.id,
      data: {
        ...node.data,
        elementUid: newElement.uid,
      }
    });
  }

  // reconnect all relations to the new element
  const outRelations = useRelationStore().byStartUid[element.uid] ?? [];
  for (const relation of outRelations) {
    useRelationStore().update({
      id: relation.id,
      startItemType: newElement.type,
      startItemId: newElement.id
    });
  }

  const inRelations = useRelationStore().byEndUid[element.uid] ?? [];
  for (const relation of inRelations) {
    if (newElement.type == "index") {
      await useRelationStore().delete({ id: relation.id, instant: true });
    } else {
      useRelationStore().update({
        id: relation.id,
        endItemType: newElement.type,
        endItemId: newElement.id
      });
    }
  }

  // reconnect all flow branches to the new element
  const flowBranches = useFlowBranchStore().byElementUid[element.uid] ?? [];
  console.log("flowBranches", flowBranches);
  for (const flowBranch of flowBranches) {
    if (newElement.type == "index") {
      await useFlowBranchStore().delete({ id: flowBranch.id, instant: true });
    } else {
      useFlowBranchStore().update({
        id: flowBranch.id,
        elementType: newElement.type,
        elementId: newElement.id
      });
    }
  }

  // switch all effects over to the new element
  const effects = useEffectStore().byAffectedUid[element.uid] ?? [];
  for (const effect of effects) {
    if (newElement.type == "index") {
      await useEffectStore().delete({ id: effect.id, instant: true });
    } else {
      useEffectStore().update({
        id: effect.id,
        affectedId: newElement.id,
        affectedType: newElement.type
      });
    }
  }

  const conditions = useConditionStore().getByActivator(element);
  for (const condition of Object.values(conditions)) {
    useConditionStore().update({
      id: condition.id,
      activatorId: newElement.id,
      activatorType: newElement.type
    });
  }

  const indexStore = useIndexStore();
  const indices = indexStore.getByLinkedElement(element.uid);
  for (const index of indices) {
    const newExpression = index.valueExpression.replaceAll(element.uid, newElement.uid);
    indexStore.update({
      id: index.id,
      valueExpression: newExpression
    });
  }

  const flowStore = useFlowStore();
  const flows = flowStore.getByLinkedElement(element.uid);
  for (const flow of flows) {
    const newExpression = flow.valueExpression.replaceAll(element.uid, newElement.uid);
    flowStore.update({
      id: flow.id,
      valueExpression: newExpression
    });
  }

  // await this.delete({ id: element.id, instant: true });
  return newElement as ItemOfStore<NT>;
}

function getElementCalculatedValue(element: TargetItem | ResourceItem | IndexItem): ExpressionValue {
  if (element.type == "target") return useTargetStore().expressionValues[element.uid];
  if (element.type == "resource") return useResourceStore().expressionValues[element.uid];
  if (element.type == "index") return useIndexStore().expressionValues[element.uid];
  throw new Error("Invalid element type");
}
